import { useState, useEffect } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { NativeSelect } from '../ui/NativeSelect';
import { Alert } from '../ui/Alert';
import { Target, FileStack, Clock, Loader2 } from 'lucide-react';
import { ListTemplate } from '@/types/lists';
import { useGoalsStore } from '@/store/goalsStore';
import { useListsStore } from '@/store/listsStore';
import { Badge } from '../ui/Badge';

interface UseTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: ListTemplate | null;
}

export function UseTemplateModal({
  isOpen,
  onClose,
  template
}: UseTemplateModalProps) {
  const { goals, isLoading: isLoadingGoals, initializeGoals } = useGoalsStore();
  const { cloneTemplate } = useListsStore();

  const [selectedGoalId, setSelectedGoalId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize goals when modal opens
  useEffect(() => {
    if (isOpen) {
      // Always initialize goals when modal opens to ensure fresh data
      initializeGoals();
      setSelectedGoalId('');
      setError(null);
    }
  }, [isOpen, initializeGoals]);

  const handleSubmit = async () => {
    if (!template) return;

    if (!selectedGoalId) {
      setError('Please select a goal to link this list to');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await cloneTemplate(template.id, {
        title: template.title,
        description: template.description,
        goalId: selectedGoalId,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create list from template');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!template) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Use Template"
      description="Create a new list from this template and link it to a goal"
      size="md"
    >
      <div className="space-y-6">
        {/* Template Preview */}
        <div className="p-4 bg-muted/50 rounded-lg space-y-3">
          <div className="flex items-start gap-3">
            {template.color && (
              <div
                className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                style={{ backgroundColor: template.color }}
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-foreground">{template.title}</h3>
                <Badge
                  variant="outline"
                  className="text-xs bg-purple-100 text-purple-800 border-purple-200"
                >
                  {template.category}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {template.description}
              </p>
            </div>
          </div>

          {/* Template Stats */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground pt-2 border-t">
            <div className="flex items-center gap-1">
              <FileStack className="h-3 w-3" />
              <span>{template.items.length} items</span>
            </div>
            {template.estimatedHours && (
              <div className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                <span>{template.estimatedHours}h estimated</span>
              </div>
            )}
          </div>
        </div>

        {/* Goal Selection */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Target className="h-4 w-4" />
            <label className="text-sm font-medium">Link to Goal *</label>
          </div>
          {isLoadingGoals ? (
            <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading goals...
            </div>
          ) : goals.length === 0 ? (
            <Alert variant="destructive">
              No goals available. Please create a goal first before using a template.
            </Alert>
          ) : (
            <NativeSelect
              value={selectedGoalId}
              onChange={(e) => setSelectedGoalId(e.target.value)}
              error={!selectedGoalId && error ? 'Goal is required' : undefined}
            >
              <option value="">Select a goal...</option>
              {goals.map(goal => (
                <option key={goal.id} value={goal.id}>
                  {goal.title}
                </option>
              ))}
            </NativeSelect>
          )}
          <p className="text-xs text-muted-foreground">
            Every list must be linked to a goal to maintain focus and track progress
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            {error}
          </Alert>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || isLoadingGoals || goals.length === 0}
          >
            {isSubmitting ? 'Creating...' : 'Create List'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
